import {
  useGetTimeZones,
  useGetCompanyEventCategories,
} from '@dispatch/shared/hooks';
import { useCurrentUser } from '@dispatch/web/auth';
import {
  EditEventForm,
  EditScheduleForm,
  ResultStatus,
} from '@dispatch/shared/types';
import {
  CheckBoxField,
  DateField,
  DateTimeField,
  Section,
  SelectField,
  TextField,
} from '@dispatch/web/ui';
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  InputAdornment,
  Stack,
  Toolbar,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { ResourceContext } from '../../ResourcesDetails';
import { isEmpty, isNil, set } from 'lodash';

const DAY_IN_MS = 86340000; // 23 hours, 59 minutes, and 0 seconds

export type EditEventProps = {
  control: Control<FieldValues>;
  errors: FieldErrors;
  onSubmit: () => void;
  upsertEventStatus: ResultStatus;
  isEdit?: boolean;
  watchEditEvent?: UseFormWatch<EditEventForm>;
  hidePostalField?: boolean;
  allowedRepeat?: number;
  isBulkUpdate?: boolean;
  getEditValues?: UseFormGetValues<EditEventForm>;
  setEditValues?: UseFormSetValue<EditEventForm>;
};

export const EditEvent = ({
  control,
  errors,
  onSubmit,
  upsertEventStatus,
  isEdit,
  watchEditEvent,
  hidePostalField = false,
  allowedRepeat,
  isBulkUpdate,
  getEditValues,
  setEditValues,
}: EditEventProps) => {
  const { showLocationBasedEvents } = React.useContext(ResourceContext);
  const [repeat, setRepeat] = useState(false);

  const { tenantId } = useCurrentUser();
  const { timeZones } = useGetTimeZones(tenantId || '');
  const { eventCategories } = useGetCompanyEventCategories();
  const [hidePostalCode, setHidePostalCode] = useState(false);
  const [hideRepeatFor, setHideRepeatFor] = useState(false);
  const [hideForced, setHideForced] = useState(false);

  const showPostalCode = React.useMemo(() => {
    if (!showLocationBasedEvents) return true;
    return hidePostalField ? true : hidePostalCode;
  }, [showLocationBasedEvents, hidePostalField, hidePostalCode]);

  React.useEffect(() => {
    if (watchEditEvent?.('postalCode')) {
      setHideRepeatFor(true);
    } else {
      setHideRepeatFor(false);
    }
  }, [watchEditEvent?.('postalCode')]);

  const renderDays = (days: { label: string; name: string }[]) =>
    days.map((day: { label: string; name: string }) => {
      return (
        <Grid size={1}>
          <div hidden={!repeat || !!watchEditEvent?.('postalCode')}>
            <CheckBoxField
              control={control}
              id={day.name}
              name={`days.${day.name}`}
              label={day.label}
              data-testid={day}
            />
          </div>
        </Grid>
      );
    });

  const checkIsAllDay = useMemo(() => {
    if (!isEdit || isNil(getEditValues)) {
      return false;
    }

    const startDateTime = new Date(getEditValues()?.startDateTime);
    const endDateTime = new Date(getEditValues()?.endDateTime);

    return endDateTime.getTime() - startDateTime.getTime() >= DAY_IN_MS;
  }, [getEditValues, isEdit]);

  useEffect(() => {
    setEditValues?.('isAllDay', checkIsAllDay);
  }, [setEditValues, getEditValues, checkIsAllDay]);

  return (
    <div
      style={{
        padding: '24px',
      }}
    >
      <Section>
        <Stack>
          <Grid container spacing={2} marginBottom={2} alignItems="end">
            <Grid size={3}>
              <div hidden={watchEditEvent?.('isAllDay')}>
                <DateTimeField
                  id="startDateTime"
                  name="startDateTime"
                  label="Start Date Time"
                  control={control}
                  errors={errors}
                  required
                />
                <DateTimeField
                  id="endDateTime"
                  name="endDateTime"
                  label="End Date Time"
                  control={control}
                  errors={errors}
                  required
                />
              </div>
              <div hidden={!watchEditEvent?.('isAllDay')}>
                <DateField
                  id="startDateTime"
                  name="startDateTime"
                  label="Start Date Time"
                  control={control}
                  errors={errors}
                  required
                />
                <DateField
                  id="endDateTime"
                  name="endDateTime"
                  label="End Date Time"
                  control={control}
                  errors={errors}
                  required
                />
              </div>
              <SelectField
                id={'timeZoneId'}
                name={'timeZoneId'}
                label={'Schedule Time Zone'}
                control={control}
                options={timeZones}
                errors={errors}
                required
              />
              <SelectField
                id={'category'}
                name={'category'}
                label={'Category'}
                control={control}
                options={eventCategories.map((category: string) => {
                  return {
                    title: category,
                    value: category,
                  };
                })}
                errors={errors}
                overrideOptionsLabel={(option: any) => option?.toString() || ''}
                required
              />
              <TextField
                id="name"
                name="name"
                label="Event Name"
                control={control}
                errors={errors}
                required
              />
              {/* when bulk updating we cannot specify postal code */}
              <div hidden={showPostalCode}>
                <TextField
                  id="postalCode"
                  name="postalCode"
                  label="Postal Code"
                  control={control}
                  errors={errors}
                />
              </div>
              <div>
                <CheckBoxField
                  id="isAllDay"
                  name="isAllDay"
                  label="All Day Event"
                  control={control}
                />
              </div>
              <div hidden={hideForced || isBulkUpdate}>
                <CheckBoxField
                  id="isForced"
                  name="isForced"
                  label="Force"
                  control={control}
                  checked={(field) => {
                    setHideRepeatFor(field.value);
                    return typeof field.value === 'boolean'
                      ? field.value
                      : false;
                  }}
                />
              </div>
              <div hidden={!isEdit}>
                <TextField
                  control={control}
                  id="createdBy"
                  name="createdBy"
                  label="Created By"
                  disabled
                />
                <TextField
                  control={control}
                  id="editedBy"
                  name="editedBy"
                  label="Edited By"
                  disabled
                />
              </div>
              <div hidden={hideRepeatFor || isEdit}>
                <CheckBoxField
                  id="repeat"
                  name="repeat"
                  label="Repeat"
                  control={control}
                  checked={(field) => {
                    setRepeat(field.value);
                    setHidePostalCode(field.value);
                    setHideForced(field.value);
                    return typeof field.value === 'boolean'
                      ? field.value
                      : false;
                  }}
                  disabled={isEdit}
                />
              </div>
            </Grid>
            <Grid
              container
              spacing={0}
              size={{
                xs: 2,
                md: 12,
              }}
            >
              <div hidden={!repeat || hideRepeatFor}>
                <TextField
                  control={control}
                  id="repeatFor"
                  name="repeatFor"
                  label="Repeat For the Next"
                  type="number"
                  inputProps={{
                    inputmode: 'numeric',
                    pattern: '[0-9]*',
                    max: allowedRepeat || undefined,
                    endAdornment: (
                      <InputAdornment position="end">Days</InputAdornment>
                    ),
                  }}
                  errors={errors}
                  required
                />
              </div>
            </Grid>
            {renderDays([
              { label: 'Su', name: 'sunday' },
              { label: 'M', name: 'monday' },
              { label: 'Tu', name: 'tuesday' },
              { label: 'W', name: 'wednesday' },
              { label: 'Th', name: 'thursday' },
              { label: 'F', name: 'friday' },
              { label: 'Sa', name: 'saturday' },
            ])}
            <Grid
              hidden={!(repeat && errors && errors['days']?.message)}
              size={{
                xs: 3,
                md: 12,
              }}
            >
              <FormHelperText error={true}>
                {errors && errors['days']?.message}
              </FormHelperText>
            </Grid>
          </Grid>
          <Toolbar>
            <Box sx={{ flexGrow: 1 }} />
            <Box>
              <Button
                variant="contained"
                color="info"
                disabled={!isEmpty(errors)}
                sx={{
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  lineHeight: '22px',
                  color: 'white',
                  letterSpacing: '0.46px',
                }}
                onClick={onSubmit}
                data-testid={'edit-event-save'}
              >
                {upsertEventStatus === 'loading' ? 'Saving' : 'Save'}
              </Button>
            </Box>
          </Toolbar>
        </Stack>
      </Section>
    </div>
  );
};
